import { formatUSD, type Totals } from "../utils/acvRcv";

interface TwoCheckTimelineProps {
  totals: Totals;
}

/**
 * Animated SVG timeline showing the ACV→RCV "two-check" gap.
 * Day 0 (claim) → ~Day 30 (Check 1: ACV) → cash gap → ~Day 180 (Check 2: holdback)
 */
export default function TwoCheckTimeline({ totals }: TwoCheckTimelineProps) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-4">
        <div className="text-sm font-semibold">The two-check trap</div>
        <div className="text-xs text-muted-foreground leading-snug max-w-prose">
          RCV policies don't pay you all at once. You get ACV up front, then have to front the cash to
          replace everything before the holdback (Check 2) lands.
        </div>
      </div>

      <div className="relative pt-8 pb-4 px-4">
        {/* Track */}
        <div className="absolute left-[12%] right-[12%] top-[calc(2rem+0.5rem)] h-1 rounded-full bg-muted" />
        {/* Animated fill */}
        <div
          className="absolute left-[12%] top-[calc(2rem+0.5rem)] h-1 rounded-full bg-gradient-to-r from-emerald-500 via-amber-400 to-emerald-500 origin-left"
          style={{
            right: "12%",
            animation: "scale-x-grow 1.2s ease-out forwards",
            transformOrigin: "left center",
            transform: "scaleX(0)",
          }}
        />
        <div className="relative grid grid-cols-3 gap-2">
          <Marker
            day="Day 0"
            label="Claim filed"
            amount=""
            tone="neutral"
          />
          <Marker
            day="~Day 30"
            label="Check 1 (ACV)"
            amount={formatUSD(totals.acv)}
            tone="amber"
          />
          <Marker
            day="~Day 180"
            label="Check 2 (holdback)"
            amount={formatUSD(totals.gap)}
            tone="emerald"
          />
        </div>
      </div>

      <div className="mt-4 rounded-lg bg-rose-50/60 border border-rose-200 px-4 py-3">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-rose-700">
          You float in the meantime
        </div>
        <div className="text-sm text-rose-900/90 mt-1 leading-snug">
          You'll need to spend roughly <span className="font-semibold tabular-nums">{formatUSD(totals.rcv)}</span> to
          replace everything, but only <span className="font-semibold tabular-nums">{formatUSD(totals.acv)}</span> is in
          hand. The remaining <span className="font-semibold tabular-nums">{formatUSD(totals.gap)}</span> sits on your
          credit card until receipts are submitted — and there's a deadline (often 180 days) to claim it.
        </div>
      </div>

      <style>{`
        @keyframes scale-x-grow {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
}

function Marker({
  day,
  label,
  amount,
  tone,
}: {
  day: string;
  label: string;
  amount: string;
  tone: "neutral" | "amber" | "emerald";
}) {
  const dot = {
    neutral: "bg-muted-foreground/40",
    amber: "bg-amber-500",
    emerald: "bg-emerald-500",
  }[tone];

  return (
    <div className="flex flex-col items-center text-center animate-fade-in min-w-0 px-1">
      <div className={`relative z-10 h-4 w-4 rounded-full ring-4 ring-card ${dot}`} />
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-3">{day}</div>
      <div className="text-xs font-semibold mt-0.5 leading-tight">{label}</div>
      {amount && <div className="text-sm tabular-nums mt-0.5">{amount}</div>}
    </div>
  );
}
